<template>
  <Modal
    size="md"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          Purchase order details
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <div
          v-if="fetchPurchaseOrderAction.is(States.LOADING)"
          class="flex justify-center"
        >
          <AppSpinner class="my-8" />
        </div>

        <div
          v-else-if="fetchPurchaseOrderAction.is(States.COMPLETE) && purchaseOrder"
          class="flex flex-col space-y-4 items-center w-full relative"
        >
          <div class="grid grid-cols-[1fr_auto_1fr] w-full items-center">
            <div class="flex space-x-2">
              <template v-if="isDraft && project?.unarchived">
                <button
                  type="button"
                  class="btn btn--secondary-blue"
                  :disabled="markPaidAction.is(States.LOADING) || cancelAction.is(States.LOADING)"
                  @click="handleMarkPaid"
                >
                  Mark as paid
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-alert"
                  :disabled="markPaidAction.is(States.LOADING) || cancelAction.is(States.LOADING)"
                  @click="handleCancel"
                >
                  Cancel
                </button>
              </template>
            </div>

            <span
              v-if="statusKey"
              class="py-2 px-4 rounded-lg font-semibold text-sm border"
              :class="PurchaseOrderStatusBadgeClasses[statusKey]"
            >
              {{ PurchaseOrderStatusTitles[statusKey] }}
            </span>

            <div class="ml-auto">
              <button
                type="button"
                class="btn btn--secondary-blue"
                @click="showDetails = !showDetails"
              >
                {{ showDetails ? 'View attachments' : 'View details' }}
              </button>
            </div>
          </div>

          <div
            v-if="showDetails"
            class="flex space-x-4 w-full"
          >
            <div class="flex flex-col w-full space-y-4">
              <p class="font-semibold text-lg">
                Purchase order details
              </p>

              <p class="text-sm text-gray-600">
                Code: <span class="font-semibold text-gray-900">{{ purchaseOrder.code }}</span>
              </p>

              <form class="flex flex-col space-y-4">
                <label>
                  Date
                  <AppInput
                    v-model="purchaseOrderForm.form.date"
                    type="date"
                    name="date"
                    class="form-input"
                    :disabled="!canEdit"
                    :error="purchaseOrderForm.getErrors('date')"
                  />
                </label>

                <label>
                  Description
                  <textarea
                    v-model="purchaseOrderForm.form.description"
                    name="description"
                    rows="3"
                    class="resize-none form-input"
                    :disabled="!canEdit"
                  />
                </label>

                <label>
                  Amount
                  <AppCurrencyInput
                    v-model="purchaseOrderForm.form.amount"
                    name="amount"
                    class="form-input"
                    :disabled="!canEdit"
                    :error="purchaseOrderForm.getErrors('amount')"
                  />
                </label>

                <label>
                  Actual cost
                  <AppCurrencyInput
                    v-model="purchaseOrderForm.form.actual_cost"
                    name="actual_cost"
                    class="form-input"
                    :disabled="!canEdit"
                    :error="purchaseOrderForm.getErrors('actual_cost')"
                  />
                </label>

                <label>
                  Contractor reference
                  <AppInput
                    v-model="purchaseOrderForm.form.contractor_ref"
                    type="text"
                    name="contractor_ref"
                    class="form-input"
                    :disabled="!canEdit"
                    :error="purchaseOrderForm.getErrors('contractor_ref')"
                  />
                </label>

                <label>
                  Package
                  <select
                    v-model="purchaseOrderForm.form.package_id"
                    name="package_id"
                    class="form-input w-full"
                    :disabled="!canEdit"
                  >
                    <option :value="undefined">
                      None
                    </option>

                    <option
                      v-for="pkg in packages"
                      :key="pkg.id"
                      :value="pkg.id"
                    >
                      {{ pkg.name }}
                    </option>
                  </select>
                </label>
              </form>

              <div
                v-if="purchaseOrder.paid_at"
                class="text-sm text-gray-600"
              >
                Paid at: {{ purchaseOrder.paidAt?.toLocaleString() }}
              </div>

              <div
                v-if="purchaseOrder.cancelled_at"
                class="text-sm text-gray-600"
              >
                Cancelled at: {{ purchaseOrder.cancelledAt?.toLocaleString() }}
              </div>

              <div class="space-y-3">
                <p class="font-semibold">
                  Comments
                </p>

                <form
                  class="space-y-2"
                  @submit.prevent="saveComment"
                >
                  <textarea
                    v-model="commentForm.form.comment"
                    name="comment"
                    rows="2"
                    class="form-input resize-none w-full"
                    placeholder="Add a comment..."
                  />

                  <p
                    v-if="commentForm.hasErrors('comment')"
                    class="text-sm text-red-600"
                  >
                    {{ commentForm.getErrors('comment') }}
                  </p>

                  <button
                    type="submit"
                    class="btn btn--secondary-blue"
                    :disabled="commentAction.is(States.LOADING)"
                  >
                    Add comment
                  </button>
                </form>

                <div
                  v-if="purchaseOrder.comments?.length"
                  class="max-h-48 overflow-y-auto space-y-3"
                >
                  <div
                    v-for="comment in purchaseOrder.comments"
                    :key="comment.id"
                  >
                    <p class="font-semibold text-sm">
                      {{ comment.creator?.name }}
                      <span class="text-gray-500 font-normal">{{ comment.createdAt.toLocaleString() }}</span>
                    </p>

                    <p class="text-sm whitespace-pre-wrap break-words">
                      {{ comment.body }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="w-full"
          >
            <AppSpinner
              v-if="purchaseOrderMediaCollection.mediaRequestAction.is(States.LOADING)"
              class="mx-auto my-8"
            />

            <template v-else>
              <div
                v-if="purchaseOrderMediaCollection.mediaCollection.value.length === 0"
                class="font-semibold mb-4 text-center"
              >
                No attachments found
              </div>

              <div
                v-if="project?.unarchived"
                class="mb-4"
              >
                <FileUpload
                  ref="fileUploadForm"
                  multiple
                  @change="fileUploaded"
                />
              </div>

              <div class="relative">
                <Swiper
                  v-if="purchaseOrderMediaCollection.mediaCollection.value.length > 0"
                  :modules="[Pagination]"
                  :pagination="{ el: '.swiper-pagination-po-show', clickable: true }"
                  class="w-full"
                  @swiper="setSwiper"
                >
                  <SwiperSlide
                    v-for="media in purchaseOrderMediaCollection.mediaCollection.value"
                    :key="media.uuid"
                  >
                    <MediaDisplay :media="media" />
                  </SwiperSlide>
                </Swiper>

                <div class="swiper-pagination-po-show flex justify-center" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-between w-full space-x-4">
        <button
          type="button"
          class="btn btn--tertiary-blue"
          @click="$emit('close')"
        >
          Close
        </button>

        <button
          v-if="canEdit"
          type="button"
          class="btn btn--secondary-blue"
          :disabled="updatePurchaseOrderAction.is(States.LOADING)"
          @click="updatePurchaseOrder"
        >
          Save changes
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { type Media, useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, enumKeyFromValue } from '@/helpers';
import {
  PurchaseOrderStatus,
  PurchaseOrderStatusBadgeClasses,
  PurchaseOrderStatusTitles,
} from '@/models/PurchaseOrder';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { usePurchaseOrdersStore } from '@/store/purchaseOrders';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  purchaseOrderId: number;
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'updated'): void;
}>();

const purchaseOrdersStore = usePurchaseOrdersStore();
const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);

const purchaseOrder = computed(() => {
  return purchaseOrdersStore.models
    .with('user')
    .with('package')
    .with('comments', (query) => query.with('creator'))
    .find(props.purchaseOrderId);
});

const packages = computed(() => packagesStore.models.where('project_id', parseInt(props.projectId)).get());
const fetchPurchaseOrderAction = useStoreApiAction(purchaseOrdersStore.fetchPurchaseOrder);
const updatePurchaseOrderAction = useStoreApiAction(purchaseOrdersStore.updatePurchaseOrder);
const markPaidAction = useStoreApiAction(purchaseOrdersStore.markPaid);
const cancelAction = useStoreApiAction(purchaseOrdersStore.cancelPurchaseOrder);
const commentAction = useStoreApiAction(purchaseOrdersStore.comment);
const purchaseOrderMediaCollection = useMediaCollection(purchaseOrdersStore.media);
const showDetails = ref(true);

const purchaseOrderForm = useFormData({
  date: '',
  description: '',
  amount: undefined as number | undefined,
  actual_cost: undefined as number | undefined,
  contractor_ref: '',
  package_id: undefined as number | undefined,
});

const commentForm = useFormData({
  comment: '',
});

watch(purchaseOrder, (newValue) => {
  if(newValue) {
    purchaseOrderForm.setData(newValue, ['date']);
    purchaseOrderForm.form.date = dateForDateInput(newValue.date ?? new Date()) ?? '';
  }
}, { immediate: true });

watch(
  () => props.purchaseOrderId,
  async (id) => {
    showDetails.value = true;
    purchaseOrderMediaCollection.clear();
    fetchPurchaseOrderAction.request(id);
    purchaseOrderMediaCollection.fetchMedia(id);
    await packagesStore.index(props.projectId);
  },
  { immediate: true },
);

const isDraft = computed(() => purchaseOrder.value?.status === PurchaseOrderStatus.draft);
const canEdit = computed(() => isDraft.value && Boolean(project.value?.unarchived));

const statusKey = computed(() => {
  if(!purchaseOrder.value) {
    return undefined;
  }

  return enumKeyFromValue(PurchaseOrderStatus, purchaseOrder.value.status);
});

const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const storeMediaAction = useStoreApiAction(purchaseOrdersStore.storeMedia);

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files ?? []);

  files.forEach((file) => {
    const fileFormData = new FormData();

    fileFormData.append('images[]', file);

    storeMediaAction.request(props.purchaseOrderId, fileFormData).then((medias) => {
      purchaseOrderMediaCollection.addMedia(medias as unknown as Record<string, Media>);
    }).catch((error) => {
      if(error?.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to upload attachments',
          text: error.data?.message,
        });
      }
    });
  });
};

const updatePurchaseOrder = () => {
  purchaseOrderForm.resetErrors();

  updatePurchaseOrderAction.request(props.purchaseOrderId, purchaseOrderForm.form).then(() => {
    Swal.fire({ icon: 'success', text: 'Purchase order updated' });
    emit('updated');
  }).catch((error) => {
    purchaseOrderForm.setErrors(error.data);
  });
};

const handleMarkPaid = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: 'Mark as paid?',
    text: 'This purchase order will be marked as paid and can no longer be edited.',
    showCancelButton: true,
    confirmButtonText: 'Mark as paid',
    cancelButtonText: 'Cancel',
  });

  if(!result.isConfirmed) {
    return;
  }

  markPaidAction.request(props.purchaseOrderId).then(() => {
    Swal.fire({ icon: 'success', text: 'Purchase order marked as paid' });
    emit('updated');
  });
};

const handleCancel = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: 'Cancel purchase order?',
    text: 'This purchase order will be cancelled and can no longer be edited.',
    showCancelButton: true,
    confirmButtonText: 'Cancel purchase order',
    cancelButtonText: 'Keep',
  });

  if(!result.isConfirmed) {
    return;
  }

  cancelAction.request(props.purchaseOrderId).then(() => {
    Swal.fire({ icon: 'success', text: 'Purchase order cancelled' });
    emit('updated');
  });
};

const saveComment = () => {
  commentAction.request(props.purchaseOrderId, { comment: commentForm.form.comment }).then(() => {
    commentForm.form.comment = '';
    commentForm.resetErrors();
    fetchPurchaseOrderAction.request(props.purchaseOrderId);
  }).catch((error) => {
    commentForm.setErrors(error.data);
  });
};
</script>
